import React, { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import "../../ModuleSubtopic1.css";
import NavigationButtons from "../../components/NavigationButtons";
import { useSubtopicNavigation } from "../../hooks/useSubtopicNavigation";

// All DAA subtopics for navigation
const ALL_DAA_SUBTOPICS = [
  { name: 'Stages of Algorithm Development', path: '/daa-m1-s1' },
  { name: 'Greedy techniques: Fractional Knapsack Problem', path: '/daa-m1-s2' },
  { name: 'Greedy techniques: Huffman coding', path: '/daa-m1-s3' },
  { name: 'Divide and Conquer: Maximum Subarray', path: '/daa-m1-s4' },
  { name: 'Divide and Conquer: Karatsuba faster integer multiplication algorithm', path: '/daa-m1-s5' },
  { name: 'Dynamic programming: Matrix Chain Multiplication', path: '/daa-m2-s1' },
  { name: 'Dynamic programming: Assembly Line Scheduling', path: '/daa-m2-s2' },
  { name: 'Dynamic programming: Longest Common Subsequence (LCS)', path: '/daa-m2-s3' },
  { name: 'Dynamic programming: 0-1 Knapsack', path: '/daa-m2-s4' },
  { name: 'Backtracking: N-Queens problem', path: '/daa-m2-s5' },
  { name: 'Backtracking: Subset Sum', path: '/daa-m2-s6' },
  { name: 'Backtracking: Graph Coloring', path: '/daa-m2-s7' },
  { name: 'Branch & Bound: LIFO-BB and FIFO BB methods: Job Selection problem', path: '/daa-m2-s8' },
  { name: 'Branch & Bound: 0-1 Knapsack Problem', path: '/daa-m2-s9' },
  { name: 'Naive String-Matching', path: '/daa-m3-s1' },
  { name: 'KMP Algorithm', path: '/daa-m3-s2' },
  { name: 'Rabin-Karp Algorithm', path: '/daa-m3-s3' },
  { name: 'Bellman-ford', path: '/daa-m4-s1' },
  { name: 'Floyd Warshall', path: '/daa-m4-s2' },
  { name: 'Ford-Fulkerson', path: '/daa-m4-s3' },
  { name: 'Edmond Karp', path: '/daa-m4-s4' },
  { name: 'Push Relabel', path: '/daa-m4-s5' },
  { name: 'Line Segments: Properties, Intersection, sweeping lines', path: '/daa-m5-s1' },
  { name: 'Convex Hull: Graham\'s Scan', path: '/daa-m5-s2' },
  { name: 'Convex Hull: Jarvis\' March Algorithm', path: '/daa-m5-s3' },
  { name: 'Randomized Quick Sort', path: '/daa-m6-s1' },
  { name: 'Hiring Problem', path: '/daa-m6-s2' },
  { name: 'Global Minimum Cut Problem', path: '/daa-m6-s3' }
];

const VideoList = ({ videos, onVideoClick }) => (
  <div className="subtopic-video-list">
    {videos.map((video, index) => (
      <div
        key={index}
        className="subtopic-video-wrapper"
        onClick={() => onVideoClick(video)}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => e.key === "Enter" && onVideoClick(video)}
      >
        <iframe
          src={video}
          allowFullScreen
          title={`Video ${index + 1}`}
          className="subtopic-video-frame"
        ></iframe>
      </div>
    ))}
  </div>
);

const DAAModuleSubtopic = ({ title, videos, takeaways, questions, exampleVideos, subtopics, subjectPath }) => {
  const [expandedVideo, setExpandedVideo] = useState(null);
  const location = useLocation();

  // Use passed subtopics or fallback to hardcoded list
  const navSubtopics = useMemo(() => {
    return (subtopics && subtopics.length > 0) ? subtopics : ALL_DAA_SUBTOPICS;
  }, [subtopics]);

  const { prevPath, nextPath, handleBack } = useSubtopicNavigation(
    navSubtopics,
    location.pathname,
    subjectPath || '/subject-DAAModules'
  );
  return (
    <div className="subtopic-main">
      <div className="subtopic-container">
        <div className="subtopic-header">{title}</div>

        {videos.length > 0 && (
          <div className="subtopic-section">
            <h2>Videos</h2>
            <VideoList videos={videos} onVideoClick={setExpandedVideo} />
          </div>
        )}

        {takeaways.length > 0 && (
          <div className="subtopic-section">
            <h2>Key Takeaways</h2>
            {takeaways.map((item, index) => (
              <div key={index} className="takeaway-item">
                <p>- {item.text}</p>
                {item.image && <img src={item.image} alt={`Takeaway ${index + 1}`} className="takeaway-image" />}
              </div>
            ))}
          </div>
        )}

        {questions.length > 0 && (
          <div className="subtopic-section">
            <h2>Previous Questions</h2>
            {questions.map((question, index) => (
              <div key={index} className="question-item">
                <p>- {question.text}</p>
                {question.image && <img src={question.image} alt={`Question ${index + 1}`} className="question-image" />}
              </div>
            ))}
          </div>
        )}

        {exampleVideos.length > 0 && (
          <div className="subtopic-section">
            <h2>Example Question Videos</h2>
            <VideoList videos={exampleVideos} onVideoClick={setExpandedVideo} />
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <NavigationButtons
        prevPath={prevPath}
        nextPath={nextPath}
        onBack={handleBack}
        subjectPath={subjectPath || '/subject-DAAModules'}
      />

      {/* Video Modal Overlay */}
      {expandedVideo && (
        <div
          className="video-modal-overlay"
          onClick={() => setExpandedVideo(null)}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === "Escape" && setExpandedVideo(null)}
        >
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="video-modal-close"
              onClick={() => setExpandedVideo(null)}
              aria-label="Close video"
            >
              ✕
            </button>
            <iframe
              src={expandedVideo}
              allowFullScreen
              title="Expanded Video"
              className="video-modal-iframe"
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
};

export default DAAModuleSubtopic;
